import { useParams } from "react-router-dom";
import { categoryLabel, findProduct, listProducts } from "../lib/productsApi";
import { img } from "../data/images";
import { APS_URL, CONTACT_EMAIL } from "../data/chapters";
import { useStripeCheckout } from "../../hooks/useStripeCheckout";
import { TLink } from "../components/TransitionProvider";
import ImageWithCaption from "../components/ImageWithCaption";
import Button from "../components/Button";
import Footer from "../components/Footer";

const STEPS = {
  subscription: [
    ["Subscribe", "Check out securely with Stripe. It takes a couple of minutes."],
    ["Get oriented", "Your member dashboard opens with your intake form and project roadmap."],
    ["Work together", "Book your weekly working session and watch your roadmap move."],
  ],
  one_time: [
    ["Book", "Check out securely with Stripe. It's a single payment, with nothing recurring."],
    ["Get oriented", "Your dashboard opens so you can share context before we meet."],
    ["Meet", "Book your session, then keep everything we produce."],
  ],
};

function NotFoundProduct() {
  return (
    <section className="ph">
      <p className="t-cap ph-kicker">Products</p>
      <h1 className="t-display ph-title"><span className="mask"><span className="rise">Not found.</span></span></h1>
      <p className="t-lg ph-intro">We couldn't find that product. It may have moved.</p>
      <div style={{ marginTop: 32 }}><Button variant="primary" to="/products" label="Products">Back To Products</Button></div>
    </section>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const p = findProduct(slug);
  const { checkout, loading, error } = useStripeCheckout();
  if (!p) return <NotFoundProduct />;

  const related = listProducts(p.category).filter((x) => x.id !== p.id).slice(0, 2);
  const steps = STEPS[p.billing] || STEPS.subscription;

  return (
    <>
      <section className="pp">
        <nav className="pp-crumbs t-cap" aria-label="Breadcrumb" data-rv="">
          <TLink to="/products" label="Products">Products</TLink><span>/</span><span>{categoryLabel(p.category)}</span>
        </nav>

        <div className="pp-head">
          <div className="pp-title-col">
            <p className="t-cap" data-rv="">{p.number} — {p.tag}</p>
            <h1 className="t-display pp-title">
              <span className="mask"><span className="rise" style={{ "--d": "100ms" }}>{p.name}</span></span>
            </h1>
            <p className="t-lg pp-summary" data-rv="" style={{ "--d": "260ms" }}>{p.summary}</p>
          </div>

          <div className="pp-buy" data-rv="" style={{ "--d": "340ms" }}>
            <div className="pp-price"><span className="pp-amount">{p.price}</span><span className="t-cap">{p.cadence}</span></div>
            <Button variant="primary" onClick={() => checkout(p.priceKey)} disabled={loading} label="Checkout">{loading ? "Opening secure checkout…" : p.cta}</Button>
            <Button variant="text" href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`Question about ${p.name}`)}`}>Ask a question first</Button>
            {error && <p className="pp-error t-cap" role="alert">{error}. Please try again, or write to us.</p>}
            <p className="t-cap pp-secure">Secure checkout by Stripe</p>
          </div>
        </div>

        <ImageWithCaption className="pp-figure" image={img("caption")} index="Fig. 04" title={p.name} text="A photograph that shows this service in the hands of real people goes here." parallax />

        <div className="pp-grid">
          <div className="pp-col">
            <p className="t-cap" data-rv="">What's included</p>
            <ol className="incl">
              {p.includes.map((it, i) => (
                <li key={it} data-rv="" style={{ "--d": `${i * 50}ms` }}>
                  <span className="t-cap">{String(i + 1).padStart(2, "0")}</span>
                  <span className="t-lg">{it}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="pp-col">
            <p className="t-cap" data-rv="">About this service</p>
            <p className="t-lg pp-desc" data-rv="">{p.description}</p>
            {p.aps && (
              <p className="pp-aps t-body" data-rv="">
                Working in a regulated environment? Our <a href={APS_URL} target="_blank" rel="noopener noreferrer">Advanced Platform Services</a> team builds HIPAA-conscious, SOC 2-aligned platforms at enterprise scale.
              </p>
            )}
          </div>
        </div>

        <div className="steps">
          <p className="t-cap" data-rv="">How it works</p>
          <ol>
            {steps.map(([t, d], i) => (
              <li key={t} data-rv="" style={{ "--d": `${i * 80}ms` }}>
                <span className="steps-num">{i + 1}</span>
                <h3 className="t-h3">{t}</h3>
                <p className="t-body">{d}</p>
              </li>
            ))}
          </ol>
        </div>

        {related.length > 0 && (
          <div className="related">
            <p className="t-cap" data-rv="">Also in {categoryLabel(p.category)}</p>
            <ul className="rows">
              {related.map((r) => (
                <li key={r.id} data-rv="">
                  <TLink to={`/products/${r.id}`} label={r.name} className="row">
                    <span className="row-num t-cap">{r.number}</span>
                    <span className="row-name t-h2">{r.name}</span>
                    <span className="row-tag t-cap">{r.tag}</span>
                    <span className="row-price t-lg">{r.price}<em>{r.cadence}</em></span>
                    <span className="row-arrow" aria-hidden="true">→</span>
                  </TLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
      <Footer heading="Questions first?" text="Write to us. A real person will answer, before you commit to anything." />
    </>
  );
}
